import { useMutation } from '@apollo/client';
import { SetStateAction, useCallback, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import theme from '@styles/theme';
import { UPDATE_ADMIN_PW, UPDATE_INIT_ADMIN_PW } from '@graphql/mutations';
import { Mutation } from '@graphql/types';
import { userLogout } from '@modules/authentication';
import useEnter from '@hooks/useEnter';
import { validateChangePw } from '@utils/validation';

import StyledModal from '@components/styled/StyledModal';
import StyledPassword from '@components/styled/StyledPassword';
import StyledButton from '@components/styled/StyledButton';

type ChangePasswordModalProps = {
    firstLogin?: boolean;
    changePw: boolean;
    setChangePw: React.Dispatch<SetStateAction<boolean>>;
};

const ChangePasswordModal = ({
    firstLogin,
    changePw,
    setChangePw
}: ChangePasswordModalProps) => {
    const onEnter = useEnter();
    const [inputs, setInputs] = useState({
        currentPw: '',
        newPw: '',
        confirmPw: ''
    });
    const [validation, setValidation] = useState({
        minLength: false,
        hasRequiredChars: false,
        hasNoRepeatedChars: false
    });

    const [updateAdminPw] =
        useMutation<Pick<Mutation, 'updateAdminPw'>>(UPDATE_ADMIN_PW);
    const [updateInitAdminPw] =
        useMutation<Pick<Mutation, 'updateInitAdminPw'>>(UPDATE_INIT_ADMIN_PW);

    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInputs((prev) => ({
            ...prev,
            [name]: value
        }));
        if (name === 'newPw') setValidation(validatePassword(value));
    }, []);

    // 비밀번호 유효성 검사
    const validatePassword = useCallback((password: string) => {
        const hasRequiredChars =
            /(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/.test(
                password
            );
        const hasNoRepeatedChars = !/(\w)\1/.test(password);
        return {
            minLength: password.length >= 8,
            hasRequiredChars,
            hasNoRepeatedChars
        };
    }, []);

    const submitHandler = async () => {
        const { currentPw, newPw, confirmPw } = inputs;
        const { minLength, hasRequiredChars, hasNoRepeatedChars } = validation;
        if (!validateChangePw(firstLogin as boolean, inputs)) return;

        if (
            !minLength ||
            !hasRequiredChars ||
            !hasNoRepeatedChars ||
            newPw !== confirmPw
        ) {
            toast.warning('비밀번호를 정확하게 입력해주세요.');
            return;
        }
        if (!firstLogin) {
            try {
                await updateAdminPw({
                    variables: {
                        currentPw,
                        updatePw: newPw
                    },
                    onCompleted: () => {
                        alert('로그아웃 후 다시 로그인해주세요.');
                        userLogout();
                    }
                });
            } catch (err) {
                console.error(err);
            }
        } else {
            try {
                await updateInitAdminPw({
                    variables: {
                        password: newPw,
                        passwordConfirm: confirmPw
                    },
                    onCompleted: () => {
                        alert('로그아웃 후 다시 로그인해주세요.');
                        userLogout();
                    }
                });
            } catch (err) {
                console.error(err);
            }
        }
    };

    return (
        <StyledModal
            title={`비밀번호 변경${firstLogin ? '' : ' (180일 경과)'}`}
            marginBottom={28}
            isOpen={changePw}
            closeBtnHandler={() => {
                setChangePw(false);
            }}
        >
            <FormWrapper onSubmit={(e) => e.preventDefault()}>
                {!firstLogin && (
                    <>
                        <InputTitle>현재 비밀번호</InputTitle>
                        <StyledPassword
                            name="currentPw"
                            value={inputs.currentPw}
                            onChange={onChange}
                            placeholder="현재 비밀번호"
                            margin="0 0 24px"
                        />
                    </>
                )}
                <InputTitle>새로운 비밀번호</InputTitle>
                <StyledPassword
                    name="newPw"
                    value={inputs.newPw}
                    onChange={onChange}
                    placeholder="변경할 비밀번호"
                    margin="0 0 8px"
                />
                <StyledPassword
                    name="confirmPw"
                    value={inputs.confirmPw}
                    onChange={onChange}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                        onEnter(e, submitHandler)
                    }
                    placeholder="변경할 비밀번호 확인"
                    margin="0 0 12px"
                />
                <Condition>
                    <List
                        $isChecked={
                            inputs.newPw === '' ? null : validation.minLength
                        }
                    >
                        - 비밀번호 8자리 이상
                    </List>
                    <List
                        $isChecked={
                            inputs.newPw === ''
                                ? null
                                : validation.hasRequiredChars
                        }
                    >
                        - 대문자, 소문자, 숫자, 특수문자 포함
                    </List>
                    <List
                        $isChecked={
                            inputs.newPw === ''
                                ? null
                                : validation.hasNoRepeatedChars
                        }
                    >
                        - 반복되는 문자 사용 금지
                    </List>
                    <List
                        $isChecked={
                            inputs.confirmPw === ''
                                ? null
                                : inputs.newPw !== '' &&
                                  inputs.newPw === inputs.confirmPw
                        }
                    >
                        - 변경 비밀번호 새 비밀번호 일치
                    </List>
                </Condition>
                <ButtonWrapper>
                    <StyledButton
                        title="취소"
                        width={140}
                        fontColor={`${theme.colors.blackColor}`}
                        bgColor={`${theme.colors.whiteColor}`}
                        border={`${theme.colors.lightGrayBorder} 1px solid`}
                        onClick={() => {
                            setChangePw(false);
                        }}
                    />
                    <StyledButton
                        title="변경하기"
                        width={140}
                        onClick={submitHandler}
                    />
                </ButtonWrapper>
            </FormWrapper>
        </StyledModal>
    );
};

export default ChangePasswordModal;

const FormWrapper = styled.form``;

const InputTitle = styled.span`
    display: inline-block;
    margin-bottom: 8px;
    font-size: 14px;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: end;
    gap: 8px;
    margin-top: 32px;
`;

const Condition = styled.ul`
    font-size: 14px;
    line-height: 1.5;
`;

const List = styled.li<{ $isChecked?: boolean | null }>`
    color: ${({ $isChecked }) =>
        $isChecked === null
            ? theme.colors.deepGrayFontColor
            : $isChecked
            ? theme.colors.activeSkyBlue
            : theme.colors.activeOrange};
`;
